import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAgency } from "../contexts/AgencyContext";
import { useAuth } from "../contexts/AuthContext";
import { useClipboard } from "../hooks/useClipboard";
import StatusPill from "../components/StatusPill";
import { 
  Briefcase, Users, TrendingUp, Clock, ArrowUpRight, Copy, Check,
  FileWarning, CalendarClock, UserCheck, Inbox, Sparkles, ExternalLink
} from "lucide-react";

// Helper: Get time-of-day greeting
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

export default function Dashboard() {
  const { agency } = useAgency();
  const { user } = useAuth();
  const [priorities, setPriorities] = useState(null);
  const [loading, setLoading] = useState(true);
  const { copied, copyToClipboard } = useClipboard();

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/hiring/dashboard/priorities");
        setPriorities(data);
      } catch {
        /* ignore */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const counts = priorities?.counts || {};
  const totalActions = (counts.awaiting_review || 0) + (counts.interviews_today || 0) + (counts.missing_documents || 0);

  const handleCopyLink = () => {
    copyToClipboard(`${window.location.origin}/apply/${agency?.id}`);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-10">
      {/* Personalized Welcome Header */}
      <header className="mb-8" data-testid="dashboard-header">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="heading-display" data-testid="dashboard-title">
              {getGreeting()}, {user?.name?.split(" ")[0] || "there"}
            </h1>
            <p className="text-muted-foreground mt-1 text-sm md:text-base flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-secondary border border-border capitalize">
                {user?.role?.replace(/_/g, " ")}
              </span>
              <span>at <span className="font-medium text-foreground">{agency?.name || "your agency"}</span></span>
            </p>
          </div>
          <Link to="/hiring" className="btn-primary" data-testid="go-to-hiring">
            Go to Hiring <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {loading ? (
        <div className="text-sm text-muted-foreground py-12 text-center">Loading your priorities...</div>
      ) : (
        <>
          {/* Quick Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4 mb-8">
            <PriorityStatCard 
              label="Awaiting Review" 
              value={counts.awaiting_review || 0} 
              icon={Inbox} 
              accent={counts.awaiting_review > 0}
              href="/hiring?status=application_received"
            />
            <PriorityStatCard 
              label="Interviews Today" 
              value={counts.interviews_today || 0} 
              icon={CalendarClock} 
              accent={counts.interviews_today > 0}
            />
            <PriorityStatCard 
              label="Missing Documents" 
              value={counts.missing_documents || 0} 
              icon={FileWarning} 
              warning={counts.missing_documents > 0}
            />
            <PriorityStatCard 
              label="Ready to Onboard" 
              value={counts.upcoming_onboarding || 0} 
              icon={UserCheck}
              success={counts.upcoming_onboarding > 0}
            />
          </div>

          {/* Today's Priorities Section */}
          <div className="mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-primary-c" />
              <h2 className="heading-section">Today&apos;s Priorities</h2>
              {totalActions > 0 && (
                <span className="ml-2 px-2 py-0.5 rounded-full text-xs font-semibold bg-primary-c text-white">
                  {totalActions} action{totalActions !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mb-6">
            {/* Applications Awaiting Review */}
            <PriorityCard
              title="Applications Awaiting Review"
              icon={Inbox}
              count={counts.awaiting_review}
              emptyIcon={Briefcase}
              emptyTitle="No pending reviews"
              emptyText="All caught up! New applications will appear here."
              viewAllHref="/hiring?status=application_received"
              testId="awaiting-review-card"
            >
              {(priorities?.awaiting_review || []).slice(0, 4).map((app) => (
                <Link
                  to={`/hiring/${app.id}`}
                  key={app.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-secondary/60 transition-colors group"
                  data-testid={`awaiting-${app.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate group-hover:text-primary-c transition-colors">
                      {app.first_name} {app.last_name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {app.position_applied_for || "Caregiver"} • {new Date(app.submitted_at).toLocaleDateString()}
                    </div>
                  </div>
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                </Link>
              ))}
            </PriorityCard>

            {/* Today's Interviews */}
            <PriorityCard
              title="Interviews Today"
              icon={CalendarClock}
              count={counts.interviews_today}
              emptyIcon={CalendarClock}
              emptyTitle="No interviews scheduled"
              emptyText="Your interview calendar for today is clear."
              testId="interviews-today-card"
            >
              {(priorities?.interviews_today || []).slice(0, 4).map((iv) => (
                <Link
                  to={`/hiring/${iv.application_id}`}
                  key={iv.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-secondary/60 transition-colors group"
                  data-testid={`interview-${iv.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate group-hover:text-primary-c transition-colors">
                      {iv.applicant_name || "Applicant"}
                    </div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2">
                      <span className="font-medium text-foreground">
                        {new Date(iv.scheduled_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      <span>•</span>
                      <span className="capitalize">{iv.interview_type || "In-person"}</span>
                      {iv.location && <span>• {iv.location}</span>}
                    </div>
                  </div>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${
                    iv.status === 'confirmed' ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                  }`}>
                    {iv.status}
                  </span>
                </Link>
              ))}
            </PriorityCard>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6">
            {/* Missing Documents */}
            <PriorityCard
              title="Missing Documents"
              icon={FileWarning}
              count={counts.missing_documents}
              emptyIcon={FileWarning}
              emptyTitle="All documents collected"
              emptyText="Applicants have submitted their required documents."
              testId="missing-docs-card"
            >
              {(priorities?.missing_documents || []).slice(0, 4).map((app) => (
                <Link
                  to={`/hiring/${app.id}`}
                  key={app.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-secondary/60 transition-colors group"
                  data-testid={`missing-doc-${app.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate group-hover:text-primary-c transition-colors">
                      {app.first_name} {app.last_name}
                    </div>
                    <div className="text-xs text-muted-foreground flex flex-wrap gap-1 mt-1">
                      {(app.missing || []).slice(0, 3).map((doc) => (
                        <span key={doc} className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                          {doc.replace(/_/g, " ")}
                        </span>
                      ))}
                      {(app.missing || []).length > 3 && (
                        <span className="px-1.5 py-0.5 rounded bg-secondary text-muted-foreground">
                          +{app.missing.length - 3} more
                        </span>
                      )}
                    </div>
                  </div>
                  <StatusPill status={app.status} />
                </Link>
              ))}
            </PriorityCard>

            {/* Ready for Onboarding */}
            <PriorityCard
              title="Ready to Onboard"
              icon={UserCheck}
              count={counts.upcoming_onboarding}
              emptyIcon={Users}
              emptyTitle="No pending onboarding"
              emptyText="Move accepted candidates to onboarding to see them here."
              testId="onboarding-card"
            >
              {(priorities?.upcoming_onboarding || []).slice(0, 4).map((app) => (
                <Link
                  to={`/hiring/${app.id}`}
                  key={app.id}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg hover:bg-secondary/60 transition-colors group"
                  data-testid={`onboarding-${app.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate group-hover:text-primary-c transition-colors">
                      {app.first_name} {app.last_name}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {app.tracking_code}
                    </div>
                  </div>
                  <StatusPill status={app.status} />
                </Link>
              ))}
            </PriorityCard>
          </div>

          {/* Public Application Link Card */}
          {agency && (
            <div className="surface mt-6 p-5 md:p-6" data-testid="public-link-card">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="min-w-0 flex-1">
                  <div className="overline mb-1">Share with applicants</div>
                  <div className="font-semibold">Public application link</div>
                  <div className="text-xs text-muted-foreground mt-1 break-all font-mono bg-secondary/50 px-2 py-1 rounded inline-block">
                    {window.location.origin}/apply/{agency.id}
                  </div>
                </div>
                <div className="flex gap-2">
                  <a
                    href={`/apply/${agency.id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary"
                    data-testid="open-public-application"
                  >
                    <ExternalLink className="w-4 h-4" /> Preview
                  </a>
                  <button
                    onClick={handleCopyLink}
                    className="btn-primary"
                    data-testid="copy-public-link"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? "Copied!" : "Copy link"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function PriorityStatCard({ label, value, icon: Icon, accent, warning, success, href }) {
  const content = (
    <div className={`surface p-4 md:p-5 transition-all ${href ? "hover:shadow-md hover:border-foreground/10 cursor-pointer" : ""}`}>
      <div className="flex items-start justify-between">
        <div className="overline">{label}</div>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
          accent ? "text-white bg-primary-c" : 
          warning ? "text-amber-700 bg-amber-50" :
          success ? "text-emerald-700 bg-emerald-50" :
          "text-foreground bg-secondary"
        }`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="mt-3 text-3xl font-bold tracking-tight tabular-nums" data-testid={`stat-${label.replace(/\s+/g, "-").toLowerCase()}`}>
        {value}
      </div>
    </div>
  );
  
  return href ? <Link to={href}>{content}</Link> : content;
}

function PriorityCard({ title, icon: Icon, count, children, emptyIcon: EmptyIcon, emptyTitle, emptyText, viewAllHref, testId }) {
  const hasItems = count > 0;
  
  return (
    <div className="surface p-5 md:p-6" data-testid={testId}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-muted-foreground" />
          <h3 className="font-semibold">{title}</h3>
          {hasItems && (
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-secondary text-muted-foreground">
              {count}
            </span>
          )}
        </div>
        {viewAllHref && hasItems && (
          <Link to={viewAllHref} className="text-xs text-muted-foreground hover:text-foreground transition-colors">
            View all
          </Link>
        )}
      </div>
      
      {hasItems ? (
        <div className="-mx-2 space-y-0.5">
          {children}
        </div>
      ) : (
        <div className="text-center py-8">
          <div className="w-12 h-12 rounded-full bg-secondary/60 flex items-center justify-center mx-auto mb-3">
            <EmptyIcon className="w-6 h-6 text-muted-foreground/50" />
          </div>
          <div className="font-medium text-sm">{emptyTitle}</div>
          <div className="text-xs text-muted-foreground mt-1 max-w-[200px] mx-auto">{emptyText}</div>
        </div>
      )}
    </div>
  );
}
